class LoginForm extends AjaxForm {
    constructor() {
        super();
        // Defer querying DOM children until connectedCallback to ensure children exist
        this.email = null;
        this.password = null;
        this.errorMessage = null;
        this.content = null;
        this.promo = null;
        this.submit = null;
        this.errorSummary = null;
        this.storefrontTokenElement = document.getElementById('storefront-access-token');
    }

    connectedCallback() {
        // Query elements now that the element is connected and children are parsed
        try {
            this.email = this.querySelector('[name="customer[email]"]');
            this.password = this.querySelector('[name="customer[password]"]');
            this.errorMessage = this.querySelector(".form__message.custom_error");
            this.content = this.querySelector(".content_wrapper");
            this.promo = this.querySelector(".custom_success");
            this.submit = this.querySelector("#login-submit-button");
            this.errorSummary = this.querySelector('#error-summary');

            // Listen to form submit (captures Enter key and programmatic submits)
            const form = this.querySelector('form');
            if (form) {
                form.addEventListener('submit', (e) => this.onSubmit(e));
            }

            // also keep click listener on the button (optional)
            if (this.submit) {
                this.submit.addEventListener("click", (e) => {/* click handled by form submit listener */});
            }
        } catch (err) {
            console.error('Error initializing login form elements:', err);
        }

        if (this.formSubmitted()) {
            this.hideForm();
        }
    }

    formSubmitted() {
        const params = new URLSearchParams(window.location.search);
        return params.get("customer_posted") === "true";
    }

    hideForm() {
        this.content.style.display = "none";
    }

    showPromo() {
        this.promo.style.display = "flex";
    }

    hideMailError() {
        if (this.errorMessage) {
            this.errorMessage.style.display = "none";
        }
    }

    showMailError() {
        if (this.errorMessage) {
            this.errorMessage.style.display = "flex";
        }
    }

    isValidEmail(email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    }

    showErrorIcon(field) {
        const errorIcon = field.parentElement.querySelector('.error-icon');
        if (errorIcon) {
            errorIcon.style.display = 'block';
        }
    }

    updateErrorSummary(errors) {
        if (errors.length > 0) {

            this.errorSummary.innerHTML = `
                <ul class="visually-hidden">
                    ${errors.map(error => `<li>${error}</li>`).join('')}
                </ul>
            `;
            this.errorSummary.setAttribute('role', 'alert');
            this.errorSummary.setAttribute('aria-live', 'assertive'); 
            this.errorSummary.classList.remove('visually-hidden');
            this.errorSummary.focus(); 
        } else {
            this.errorSummary.classList.add('visually-hidden');
            this.errorSummary.innerHTML = '';
        }
    }

    async onSubmit(e) {
        e.preventDefault();
        this.hideMailError();

        let isValid = true;
        let errors = [];

        this.querySelectorAll('.error-message').forEach(el => el.textContent = '');
        this.querySelectorAll('.field').forEach(el => {
            el.classList.remove('error-border');
            el.querySelector("input")?.removeAttribute("aria-invalid");
            const icon = el.querySelector(".error-icon");
            if (icon) icon.style.display = 'none';
        });

        const errorEmail = this.querySelector('#login-email-error');
        if (!this.email.value.trim()) {
            isValid = false;
            errorEmail.textContent = errorEmail.dataset.loginError;
            this.email.closest('.field').classList.add('error-border');
            this.email.setAttribute("aria-invalid", "true");
            this.showErrorIcon(this.email);
            errors.push(errorEmail.dataset.loginError);
        } else if (!this.isValidEmail(this.email.value.trim())) {
            isValid = false;
            errorEmail.textContent = errorEmail.dataset.loginErrorRequired;
            this.email.closest('.field').classList.add('error-border');
            this.email.setAttribute("aria-invalid", "true");
            this.showErrorIcon(this.email);
            errors.push(errorEmail.dataset.loginErrorRequired);
        }

        const errorPassword = this.querySelector('#login-password-error');
        if (this.password && !this.password.value.trim()) {
            isValid = false;
            errorPassword.textContent = errorPassword.dataset.loginError;
            this.password.closest('.field').classList.add('error-border');
            this.password.setAttribute("aria-invalid", "true");
            this.showErrorIcon(this.password);
            errors.push(errorPassword.dataset.loginError);
        }

        this.updateErrorSummary(errors);

        if (isValid) {
            try {
                const email = this.email?.value.trim() || '';
                const password = this.password?.value.trim() || '';
                const token = await this.requestCustomerAccessToken(email, password);

                if (token) {
                    sessionStorage.setItem('customerAccessToken', token);

                    const form = this.querySelector('form');
                    if (form) {
                        let hidden = form.querySelector('input[name="customer_access_token"]');
                        if (!hidden) {
                            hidden = document.createElement('input');
                            hidden.type = 'hidden';
                            hidden.name = 'customer_access_token';
                            form.appendChild(hidden);
                        }
                        hidden.value = token;
                    }
                }
            } catch (err) {
                console.error('Error requesting customerAccessToken:', err);
            }

            window.location.hash = "login";
            this.querySelector('form').submit();
        }
    }

    async requestCustomerAccessToken(email, password) {
        const mutation = `mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {\n  customerAccessTokenCreate(input: $input) {\n    customerAccessToken { accessToken expiresAt }\n    customerUserErrors { code field message }\n  }\n}`;

        const endpointElement = document.getElementById('storefront-endpoint');
        const endpointRaw = (endpointElement && endpointElement.value)
            ? endpointElement.value
            : (document.querySelector('meta[name="storefront-endpoint"]')?.content || '');

        const storefrontToken = (this.storefrontTokenElement && this.storefrontTokenElement.value)
            ? this.storefrontTokenElement.value
            : (document.querySelector('meta[name="storefront-access-token"]')?.content || '');

        let endpoint = endpointRaw || '/api/2024-10/graphql.json';
        try {
            if (!/^https?:\/\//i.test(endpoint)) {
                if (!endpoint.startsWith('/')) endpoint = '/' + endpoint;
                endpoint = window.location.origin + endpoint;
            }
        } catch (e) {
            endpoint = '/api/2024-10/graphql.json';
        }

        try {
            const payload = { query: mutation, variables: { input: { email, password } } };

            const res = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(storefrontToken ? { 'X-Shopify-Storefront-Access-Token': storefrontToken } : {})
                },
                body: JSON.stringify(payload)
            });

            if (!res.ok) {
                const text = await res.text();
                console.error('Storefront API returned non-OK status:', res.status, text);
                return null;
            }

            const json = await res.json();
            const data = json.data && json.data.customerAccessTokenCreate;

            if (!data) {
                console.error('Unexpected storefront API response:', json);
                return null;
            }

            if (data.customerUserErrors && data.customerUserErrors.length > 0) {
                const messages = data.customerUserErrors.map(e => e.message || 'Unknown error');
                this.updateErrorSummary(messages);
                return null;
            }

            const tokenObj = data.customerAccessToken;
            if (tokenObj && tokenObj.accessToken) {
                return tokenObj.accessToken;
            }
            return null;
        } catch (err) {
            console.error('Error calling storefront API:', err);
            return null;
        }
    }
}

customElements.define("login-form", LoginForm);